// src/pages/Home/index.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Button,
  UncontrolledAlert,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getLandingData } from "../../helpers/eirna_backend_helper";
import { FadeLoader } from "react-spinners";

const Home = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertMessages, setAlertMessages] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);

  // Check localStorage for the search query so it persists across tabs
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("homePageSearchQuery") || "";
  });
  // Get user info from Redux store
  const { user } = useSelector((state) => ({
    user: state.Account?.user || {},
  }));

  useEffect(() => {
    fetchProjects();
  }, []);

  
  useEffect(() => {
    localStorage.setItem("homePageSearchQuery", searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "homePageSearchQuery") {
        setSearchQuery(e.newValue || "");
      }
    };

    // Listen for changes made in other tabs
    window.addEventListener("storage", handleStorageChange);
    
    // Cleanup listener on unmount
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
  

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return projects;
    }

    const query = searchQuery.toLowerCase().trim();
    return projects.filter((project) => {
      // Search in display name
      if (project.display_name?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in description
      if (project.description?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in project ID
      if (project.project_id?.toString().toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }, [projects, searchQuery]);

  // Fetch projects from EIRNABio Connect API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use getLandingData function
      const response = await getLandingData();

      if (response) {
        // Show ALL flashed messages instead of filtering
        const allMessages = response.flashed_messages || [];

        // Filter out messages that were dismissed (stored in localStorage)
        const dismissed = JSON.parse(
          localStorage.getItem("dismissedAlerts") || "[]"
        );
        const filteredMsgs = allMessages.filter(
          (msg) => !dismissed.includes(msg.message)
        );

        setAlertMessages(filteredMsgs);

        const projectList = response.project_list || [];

        // Remove duplicate projects based on project_id
        const uniqueProjects = Array.from(
          new Map(projectList.map((item) => [item.project_id, item])).values()
        );

        // Save to localStorage as a JSON string so project name can be displayed on
        // other pages
        const projectMapping = {};
        uniqueProjects.forEach((project) => {
          if (project.project_id && project.display_name) {
            projectMapping[project.project_id] = project.display_name;
          }
        });
        localStorage.setItem("projectNamesMapping", JSON.stringify(projectMapping));

        setProjects(uniqueProjects);
      }
    } catch (err) {
      setError("Failed to load projects. Please try again.");
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  // Dismiss alert persistently until next login
  const dismissAlert = (messageText) => {
    setAlertMessages((prev) => prev.filter((a) => a.message !== messageText));

    // Persist dismiss state in localStorage
    const dismissed = JSON.parse(
      localStorage.getItem("dismissedAlerts") || "[]"
    );
    if (!dismissed.includes(messageText)) {
      dismissed.push(messageText);
      localStorage.setItem("dismissedAlerts", JSON.stringify(dismissed));
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Given a text string that contains some html will render it properly, used for
  // project descriptions.
  function HtmlBlock({ html }) {
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }

  const HighlightedText = ({ text, searchTerm }) => {
    if (!text || !searchTerm.trim()) return text;
    
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    
    // split() with a capture group always places matches in the odd-numbered indexes
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          // If the index is odd, it's our matched search term!
          i % 2 !== 0 ? (
            <mark
              key={i}
              style={{
                backgroundColor: "#fef3c7",
                color: "#92400e",
                padding: "0 2px",
                borderRadius: "2px",
              }}
            >
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };


  const renderProjectCard = (project, index) => (
    <Col xl={3} lg={3} md={3} sm={4} xs={6} key={`${project.project_id}-${index}`}>
      <Card
        className="project-card h-100 border-0 shadow-sm"
        style={{
          cursor: "pointer",
          transition: "all 0.2s ease",
          borderRadius: "8px",
          overflow: "hidden",
          background: "white",
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        onClick={() => {
          document.querySelectorAll(".custom-tooltip").forEach((tooltip) => {
            if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
          });
          navigate(`/projects/${project.project_id}/select-function`);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow =
            "0 8px 25px rgba(47, 158, 55, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
          document.querySelectorAll(".custom-tooltip").forEach((tooltip) => {
            if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
          });
        }}
      >
        {/* Header */}
        <div
          style={{
            height: "44px",
            background: "linear-gradient(135deg, #0A5F3E 0%, #059669 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
          }}
        >
		  <HighlightedText text={project.display_name} searchTerm={searchQuery} />
		  <i 
		    className="ri-arrow-right-line" 
		    style={{ 
		    	fontSize: "13px",
		    	marginLeft: "10px",
		    }} 
		   />
        </div>
        <CardBody
          style={{
            padding: "9px 12px 0 12px",
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
	        <div
	          style={{
	            fontSize: "11px",
	            display: "-webkit-box",
	            WebkitBoxOrient: "vertical",
	            overflow: "hidden",
	            margin: 0,
	          }}
			>
			    <strong>Organism: </strong>
			    <HighlightedText text={project.organism} searchTerm={searchQuery} />
			    <br></br>
			    <strong>Transcriptome: </strong>
			    <HighlightedText text={project.transcriptome} searchTerm={searchQuery} />
			    <br></br>
				<strong>Files: </strong>
			    <HighlightedText text={project.file_summary} searchTerm={searchQuery} />
				{Array.isArray(project.access_msgs) &&
				  project.access_msgs.map((msg, idx) => (
				    <React.Fragment key={idx}>
				      <br />
				      <strong>Access: </strong>
				      <HighlightedText
				        text={msg}
				        searchTerm={searchQuery}
				      />
				    </React.Fragment>
				  ))}
			    {project.description != null && (
			      <>
				      <br></br>
				      <strong>Description:</strong>
					  <HtmlBlock html={project.description} />
				  </>
				 )}
			</div>
        </CardBody>
      </Card>
    </Col>
  );

  // Function to truncate message and determine if "Read More" is needed
  const truncateMessage = (message, maxLength = 120) => {
    if (message.length <= maxLength) {
      return { truncated: message, needsReadMore: false };
    }

    // Find the last space before maxLength to avoid cutting words
    let truncated = message.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    if (lastSpace > maxLength * 0.7) {
      // Only use space if it's not too far back
      truncated = truncated.substring(0, lastSpace);
    }

    return { truncated: truncated + "...", needsReadMore: true };
  };

  const cleanMessage = (message) => {
    if (message.includes("Your account has not yet been assigned")) {
      return "Your account has not yet been assigned to a group. Please contact the project lead for account access.";
    }
    return message.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  };

  // Function to get appropriate icon based on message type and content
  const getAlertIcon = (msg) => {
    if (msg.type === "error") {
      return "ri-error-warning-line";
    } else if (msg.type === "success") {
      return "ri-checkbox-circle-line";
    } else if (msg.type === "warning") {
      return "ri-alert-line";
    } else if (msg.type === "info") {
      return "ri-information-line";
    } else {
      // Default based on content
      if (msg.message.includes("logged in")) {
        return "ri-checkbox-circle-line";
      } else if (msg.message.includes("not yet been assigned")) {
        return "ri-error-warning-line";
      } else {
        return "ri-information-line";
      }
    }
  };

  // Function to get appropriate alert color based on message type
  const getAlertColor = (msg) => {
    switch (msg.type) {
      case "error":
        return "danger";
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "info":
        return "info";
      default:
        // Default based on content
        if (msg.message.includes("logged in")) {
          return "success";
        } else if (msg.message.includes("not yet been assigned")) {
          return "danger";
        } else {
          return "info";
        }
    }
  };

  // Open modal with full message
  const openMessageModal = (msg) => {
    setModalMessage(msg);
    setIsModalOpen(true);
  };

  // Close modal
  const closeMessageModal = () => {
    setIsModalOpen(false);
    setModalMessage(null);
  };

  document.title = "Home | EIRNABio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Alerts section - positioned inside container using UncontrolledAlert */}
          {alertMessages.length > 0 && (
            <Row className="mb-4">
              <Col lg={12}>
                <div className="alerts-container">
                  {alertMessages.map((msg, index) => {
                    const cleanedMessage = cleanMessage(msg.message);
                    const { truncated, needsReadMore } =
                      truncateMessage(cleanedMessage);

                    return (
                      <UncontrolledAlert
                        key={`${msg.message}-${index}`}
                        color={getAlertColor(msg)}
                        className="alert-label-icon label-arrow material-shadow mb-3"
                        toggle={() => dismissAlert(msg.message)}
                      >
                        <i className={`${getAlertIcon(msg)} label-icon`} />
                        <div
                          style={{ whiteSpace: "pre-line", fontSize: "13px" }}
                        >
                          <div
                            style={{
                              whiteSpace: "normal",
                              fontSize: "13px",
                              lineHeight: "1.4",
                            }}
                          >
                            {truncated}
                            {needsReadMore && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 ms-2"
                                style={{
                                  fontSize: "12px",
                                  textDecoration: "underline",
                                  fontWeight: "500",
                                  color: "inherit",
                                  opacity: 0.8,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent alert from closing
                                  openMessageModal(msg);
                                }}
                              >
                                Read more
                              </Button>
                            )}
                          </div>
                        </div>
                      </UncontrolledAlert>
                    );
                  })}
                </div>
              </Col>
            </Row>
          )}

          {/* Header with Search */}
          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h1
                    style={{
                      fontSize: "26px",
                      fontWeight: "700",
                      color: "#1f2937",
                      marginBottom: "4px",
                    }}
                  >
                    Select Project
                  </h1>
                </div>



                {/* Search Box & hide expired button */}
                {!loading && projects.length > 0 && (
                  <div style={{ minWidth: "300px", maxWidth: "400px" }}>

                  	{/*Show/Hide expired projects button */}


					{/*Search Bar*/}
                    <InputGroup
                      style={{
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      }}
                    >

                    <i
					  className={hideExpired ? "ri-hourglass-fill": "ri-hourglass-line"}
					  style={{
					    fontSize: "24px",
					    color: hideExpired ? "#0d6241" : "#9ca3af",
					    cursor: "pointer",
					    marginRight: "10px",
					    marginLeft: "10px",
					    marginTop: "2px",
					  }}
					  onClick={() => setHideExpired(prev => !prev)}
					  role="button"
					  aria-pressed={hideExpired}
					  aria-label={hideExpired ? "Click to show expired projects" : "Click to hide expired projects"}
					  title={hideExpired ? "Click to show expired projects" : "Click to hide expired projects"}
					/>
                      <InputGroupText
                        style={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRight: "none",
                        }}
                      >
                        <i className="ri-search-line text-muted" />
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderLeft: "none",
                          fontSize: "14px",
                          padding: "8px 12px",
                        }}
                      />
                      {searchQuery && (
                        <InputGroupText
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e5e7eb",
                            borderLeft: "none",
                            cursor: "pointer",
                          }}
                          onClick={clearSearch}
                        >
                          <i className="ri-close-line text-muted" />
                        </InputGroupText>
                      )}
                    </InputGroup>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {loading && (
            <div className="loading-overlay">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "50vh",
                }}
              >
                <FadeLoader color="#1A6D52" size={50} />
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Row>
              <Col lg={12}>
                <Alert
                  color="danger"
                  className="d-flex align-items-center border-0 shadow-sm"
                  style={{ borderRadius: "12px" }}
                >
                  <i className="ri-error-warning-line me-2" />
                  {error}
                  <Button
                    color="danger"
                    size="sm"
                    outline
                    className="ms-auto"
                    onClick={fetchProjects}
                  >
                    Retry
                  </Button>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Projects Grid */}
          {!loading && !error && (
            <>
              {filteredProjects.length > 0 ? (
                <Row className="g-4">
                  {filteredProjects.filter((p) => !hideExpired || !p.all_expired)
                  .map((project, index) => renderProjectCard(project, index))}
                </Row>
              ) : projects.length > 0 ? (
                // No search results
                <Row>
                  <Col lg={12} className="text-center py-5">
                    <div
                      style={{
                        padding: "60px 40px",
                        backgroundColor: "white",
                        borderRadius: "20px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          backgroundColor: "#f3f4f6",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px",
                        }}
                      >
                        <i
                          className="ri-search-line"
                          style={{ fontSize: "36px", color: "#9ca3af" }}
                        />
                      </div>
                      <h5
                        style={{
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        No projects found
                      </h5>
                      <p
                        className="text-muted mb-4"
                        style={{ fontSize: "16px" }}
                      >
                        No projects match your search for{" "}
                        <strong>"{searchQuery}"</strong>
                      </p>
                      <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={clearSearch}
                      >
                        <i className="ri-close-line me-1" />
                        Clear search
                      </Button>
                    </div>
                  </Col>
                </Row>
              ) : (
                // No projects at all
                <Row>
                  <Col lg={12} className="text-center py-5">
                    <div
                      style={{
                        padding: "60px 40px",
                        backgroundColor: "white",
                        borderRadius: "20px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          backgroundColor: "#f3f4f6",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 24px",
                        }}
                      >
                        <i
                          className="ri-folder-open-line"
                          style={{ fontSize: "36px", color: "#9ca3af" }}
                        />
                      </div>
                      <h5
                        style={{
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        No Projects Available
                      </h5>
                      <p
                        className="text-muted mb-4"
                        style={{ fontSize: "16px" }}
                      >
                        You don't have access to any projects yet. Contact your
                        administrator for project access.
                      </p>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
      </div>

      {/* Modal for full message */}
      <Modal isOpen={isModalOpen} toggle={closeMessageModal} size="lg" centered>
        <ModalHeader toggle={closeMessageModal}>
          <div className="d-flex align-items-center">
            <i
              className={`${
                modalMessage
                  ? getAlertIcon(modalMessage)
                  : "ri-information-line"
              } me-2`}
              style={{ fontSize: "18px" }}
            />
            <span>Message Details</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {modalMessage && (
            <div
              className={`alert alert-${getAlertColor(modalMessage)} mb-0`}
              style={{
                borderRadius: "8px",
                fontSize: "14px",
                lineHeight: "1.5",
                whiteSpace: "pre-wrap",
              }}
            >
              <i className={`${getAlertIcon(modalMessage)} me-2`} />
              {cleanMessage(modalMessage.message)}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeMessageModal} size="sm">
            <i className="ri-close-line fs-13 me-1"></i> Close
          </Button>
          {modalMessage && (
            <Button
              color={getAlertColor(modalMessage)}
              outline
              size="sm"
              onClick={() => {
                dismissAlert(modalMessage.message);
                closeMessageModal();
              }}
            >
              <i className="ri-error-warning-line fs-13 me-1"></i> Dismiss Alert
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Custom Styles */}
      <style jsx>{`
        .project-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.15) !important;
        }
        .project-card {
          transition: all 0.3s ease;
        }
        .alerts-container {
          animation: slideDown 0.3s ease-out;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export default Home;
